import "./HeatmapTable.scss";
import clsx from "clsx";
import { ContentRenderer } from "../ContentRenderer";
import { DocumentedComponent } from "../../../shared/components/DocumentedComponent";
import { getDataProps } from "../../../shared/utilities";

interface HeatmapTableCell {
    foregroundColor?: string,
    backgroundColor?: string,
    content: string
}

interface HeatmapTableColumn {
    title: string;
    children?: HeatmapTableColumn[];
}

interface HeatmapTableRow {
    title: string;
    cells: HeatmapTableCell[]
}

interface HeatmapTableProps {
    name?: string;
    columns: HeatmapTableColumn[];
    rows: HeatmapTableRow[]
    className?: string;
}

interface FlattenedColumn {
    column: HeatmapTableColumn;
    depth: number;
}

function getFlattenedColumns(columns: HeatmapTableColumn[]): FlattenedColumn[] {
    let result = [];

    function flattenColumn(column: HeatmapTableColumn, currentDepth: number) {
        result.push({
            column,
            depth: currentDepth
        });

        column?.children?.forEach(c => flattenColumn(c, currentDepth + 1));
    }

    columns.forEach(c => flattenColumn(c, 1));
    return result;
}

function getColumnSpan(column: HeatmapTableColumn) {
    let result = 0;

    function populateColSpan(column: HeatmapTableColumn) {
        if (!column?.children?.length) {
            result++;
        }
        else {
            column.children.forEach(populateColSpan);
        }
    }

    populateColSpan(column);
    return Math.max(1, result);
}

function HeatmapTable(props: HeatmapTableProps) {
    const { columns, rows, className } = props;
    const flattenedColumns = getFlattenedColumns(columns);
    const headerDepth = flattenedColumns.reduce((acc, elem) => Math.max(acc, elem.depth), 0);

    return (
        <table className={clsx("fill", "table", "compact", "heatmap-table", className)} {...getDataProps(props)}>
            <thead>
                {
                    Array.from(Array(headerDepth).keys()).map(currentDepth =>
                        <tr>
                            {currentDepth === 0 && <th rowSpan={headerDepth}></th>}
                            {flattenedColumns.filter(c => c.depth === currentDepth + 1).map(c => <th colSpan={getColumnSpan(c.column)}>{c.column.title}</th>)}
                        </tr>
                    )
                }
            </thead>
            <tbody>
                {
                    rows.map(row => <tr>
                        <th className="font-weight-normal">
                            {row.title}
                        </th>
                        {
                            row.cells.map(cell =>
                                <td style={{ backgroundColor: cell.backgroundColor, color: cell.foregroundColor }}>
                                    <ContentRenderer className="data-cell" input={cell.content} />
                                </td>)
                        }
                    </tr>)
                }
            </tbody>
        </table>
    );
}

HeatmapTable.defaultProps = {
    columns: [],
    rows: []
};

(HeatmapTable as DocumentedComponent).metadata = {
    description: "The heatmap table component is used to display a collection of heatmap values in tabular form.",
    isSelfClosing: true,
    attributes: [
        { name: "name", type: "string", description: "If provided, the underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value. Optional - no default value." },
        {
            name: "columns",
            type: "object",
            description: 
            `The columns to use for the table.  Can be nested to an arbitrary depth via the \`children\` field. See below for the structure of \`HeatmapColumn\`.

## \`HeatmapColumn\` Props:
            
| Name | Type | Description |
|------|------|-------------|
| \`title\` | \`string\` | The text to display in the column header. |
| \`children\` | \`HeatmapColumn[]\` | The (optional) child columns to nest inside this one. |`
        },
        {
            name: "rows",
            type: "object",
            description: 
            `The rows to render in the table.  See below for the structure of \`HeatmapRow\`.

## \`HeatmapRow\` Props:
            
| Name | Type | Description |
|------|------|-------------|
| \`title\` | \`string\` | The text to display in the row header. |
| \`cells\` | \`HeatmapCell[]\` | The cells to render in this row.  Length should match the leaf node count of the column tree.  See below for the structure of \`HeatmapCell\`. |
            
## \`HeatmapCell\` Props:
            
| Name | Type | Description |
|------|------|-------------|
| \`backgroundColor\` | \`string\` | The (optional) background color to be used for the cell.  Supports all standard CSS color formats (named color, \`rgb(xxx, xxx, xxx)\`, \`#xxxxxx\` and \`rgba(xxx, xxx, xxx, xxx)\`). |
| \`foregroundColor\` | \`string\` | The (optional) foreground color to be used for the cell content.  Supports all standard CSS color formats (named color, \`rgb(xxx, xxx, xxx)\`, \`#xxxxxx\` and \`rgba(xxx, xxx, xxx, xxx)\`). |
| \`content\` | \`string\` | The content to render in the cell.  Supports plain text, HTML, JSX and Markdown via the relevant prefix. |`
        }
    ]
};

export default HeatmapTable;